/**
 * Training Card Component
 *
 * Mobile-first training assignment card using OperatorCard as wrapper.
 * Displays material info, progress bar, due date, and status badge.
 * No hover effects — uses active: press states via OperatorCard.
 *
 * Phase 25.4 additions:
 *  - `materialType === 'quiz'` routes to `/operator/practice/quiz/:sectionId`
 *    (Phase 21.6 quiz player) instead of the TrainingViewer. The quiz sentinel
 *    has no S3 object, so routing to TrainingViewer would 404 on presigned URL.
 *  - `variant === 'refresher'` reframes the card as a weekly skill-rotation
 *    rather than a targeted remediation. No dismiss affordance (compulsory).
 *  - `variant === 'focused_drill'` adds an amber "Focused practice" badge
 *    indicating cohort-wide escalated rotation.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Video,
  FileText,
  Brain,
  Clock,
  AlertTriangle,
  CheckCircle2,
  PlayCircle,
  Sparkles,
  MessageCircle,
  Users,
  Target,
} from 'lucide-react';
import type { TrainingItem } from '../../../hooks/useOperatorPortal';
import { OperatorCard } from './OperatorCard';
import { WhyBadge } from './WhyBadge';
import { formatDuration, formatDueDate } from '../utils';

/** 48 hours in milliseconds */
const NEW_BADGE_THRESHOLD_MS = 48 * 60 * 60 * 1000;

interface TrainingCardProps {
  item: TrainingItem;
  index?: number;
}

export function TrainingCard({ item, index = 0 }: TrainingCardProps) {
  const navigate = useNavigate();
  const materialType = item.material.materialType;
  const isVideo = materialType === 'video';
  const isQuiz = materialType === 'quiz';
  const isCompleted = item.status === 'completed';
  const isInProgress = item.status === 'in_progress';
  const isNew = !isCompleted && item.createdAt &&
    (Date.now() - new Date(item.createdAt).getTime()) < NEW_BADGE_THRESHOLD_MS;

  const isRefresher = item.variant === 'refresher';
  const isFocusedDrill = item.variant === 'focused_drill';
  const isRing2 = isRefresher || isFocusedDrill;

  // Route to the quiz player for quiz-type materials. If the quiz sentinel
  // somehow has no sectionId (legacy data), degrade gracefully — disable the
  // card and surface the issue rather than navigating to a broken route.
  const quizSectionUnavailable = isQuiz && !item.sectionId;
  const href = useMemo(() => {
    if (isQuiz) {
      if (item.sectionId) {
        return `/operator/practice/quiz/${item.sectionId}`;
      }
      // No sectionId available — leave href undefined so OperatorCard renders
      // as a plain <div> (non-clickable) and we can show a disabled state.
      return undefined;
    }
    return `/operator/content/${item.assignmentId}`;
  }, [isQuiz, item.sectionId, item.assignmentId]);

  // Title + subtitle framing by variant. The underlying database `title`
  // (e.g. "Pallet Stacking Procedure") is repurposed for the refresher
  // framing; we prefix it with the rotation context.
  const displayTitle = useMemo(() => {
    const sectionLabel = item.sectionTitle ?? item.material.title;
    if (isRefresher) {
      return `Weekly skill rotation: ${sectionLabel}`;
    }
    if (isFocusedDrill) {
      return `Focused practice: ${sectionLabel}`;
    }
    return item.material.title;
  }, [item.material.title, item.sectionTitle, isRefresher, isFocusedDrill]);

  const variantSubtitle = useMemo(() => {
    if (isRefresher) {
      return 'Assigned to your shift this week';
    }
    if (isFocusedDrill) {
      return 'Your team has been practicing this area recently';
    }
    return null;
  }, [isRefresher, isFocusedDrill]);

  const getStatusBadge = () => {
    if (item.isOverdue && !isCompleted) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
          <AlertTriangle size={11} strokeWidth={1.5} />
          Overdue
        </span>
      );
    }
    if (isCompleted) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
          <CheckCircle2 size={11} strokeWidth={1.5} />
          Completed
        </span>
      );
    }
    if (isInProgress) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
          <PlayCircle size={11} strokeWidth={1.5} />
          In Progress
        </span>
      );
    }
    return (
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
        Assigned
      </span>
    );
  };

  // Material type icon + chip color. Quizzes use the same cyan treatment as
  // the "Knowledge Checks" section for visual consistency.
  const iconWrapperClass = isVideo
    ? 'bg-violet-100 text-violet-600'
    : isQuiz
      ? 'bg-cyan-100 text-cyan-600'
      : 'bg-amber-100 text-amber-600';

  const icon = isVideo ? (
    <Video size={22} strokeWidth={1.5} />
  ) : isQuiz ? (
    <Brain size={22} strokeWidth={1.5} />
  ) : (
    <FileText size={22} strokeWidth={1.5} />
  );

  const typeChipClass = isVideo
    ? 'bg-violet-50 text-violet-600'
    : isQuiz
      ? 'bg-cyan-50 text-cyan-600'
      : 'bg-amber-50 text-amber-600';

  const typeChipLabel = isVideo ? 'Video' : isQuiz ? 'Quiz' : 'Document';

  return (
    <div
      style={{
        animation: `fadeInUp 0.4s ease-out ${index < 5 ? index * 0.06 : 0}s both`,
      }}
    >
      <OperatorCard
        href={href}
        className="overflow-hidden !p-0"
      >
        {/* Progress indicator bar at top */}
        <div className="h-1.5 w-full bg-gray-100">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              isCompleted
                ? 'bg-emerald-500'
                : isInProgress
                ? 'bg-guild-green'
                : 'bg-gray-200'
            }`}
            style={{ width: `${item.progress}%` }}
          />
        </div>

        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Material Type Icon */}
            <div
              className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${iconWrapperClass}`}
            >
              {icon}
            </div>

            {/* Content */}
            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex items-start justify-between gap-2">
                <h3 className="text-base font-semibold text-op-text-primary line-clamp-2">
                  {displayTitle}
                </h3>
                <div className="flex flex-shrink-0 items-center gap-1.5">
                  {isFocusedDrill && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                      <Target size={11} strokeWidth={1.5} />
                      Focused practice
                    </span>
                  )}
                  {isRefresher && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                      <Users size={11} strokeWidth={1.5} />
                      Team
                    </span>
                  )}
                  {isNew && !isRing2 && (
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                      New
                    </span>
                  )}
                  {item.isRecommended && !isCompleted && !isRing2 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-guild-mint px-2 py-0.5 text-[11px] font-semibold text-guild-green">
                      <Sparkles size={11} strokeWidth={1.5} />
                      Recommended
                    </span>
                  )}
                  {getStatusBadge()}
                </div>
              </div>

              {/* Ring 2 subtitle framing */}
              {variantSubtitle && (
                <p className="mb-2 text-xs font-medium text-op-text-secondary">
                  {variantSubtitle}
                </p>
              )}

              {/* Default description (Ring 1 / manual assignments) */}
              {!isRing2 && item.material.description && (
                <p className="mb-2 text-sm text-op-text-secondary line-clamp-2">{item.material.description}</p>
              )}

              {/* Quiz-without-section disabled reason */}
              {quizSectionUnavailable && (
                <p className="mb-2 text-xs font-medium text-red-600">
                  Section unavailable — quiz cannot be started.
                </p>
              )}

              {/* Skill gap context — hidden for Ring 2 (cohort-wide, no
                  per-operator gap attribution) */}
              {!isCompleted && !isRing2 && (item.gapDescription || item.generatedBy) && (
                <WhyBadge
                  generatedBy={item.generatedBy}
                  sopName={item.sopName}
                  gapDescription={item.gapDescription}
                  currentScoreLabel={item.currentScoreLabel}
                  targetScoreLabel={item.targetScoreLabel}
                  variant="compact"
                />
              )}

              {/* Meta row */}
              <div className="flex flex-wrap items-center gap-3 text-xs text-op-text-tertiary">
                {/* Type badge */}
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${typeChipClass}`}>
                  {typeChipLabel}
                </span>

                {/* Duration (videos only) */}
                {item.material.duration && isVideo && (
                  <span className="flex items-center gap-1">
                    <Clock size={13} strokeWidth={1.5} />
                    {formatDuration(item.material.duration)}
                  </span>
                )}

                {/* Due date */}
                {item.dueDate && !isCompleted && (
                  <span
                    className={`flex items-center gap-1 ${
                      item.isOverdue ? 'text-red-600 font-medium' : ''
                    }`}
                  >
                    {item.isOverdue && <AlertTriangle size={13} strokeWidth={1.5} />}
                    {formatDueDate(item.dueDate)}
                  </span>
                )}

                {/* Progress */}
                {!isCompleted && item.progress > 0 && (
                  <span className="font-medium text-guild-green">{item.progress}% complete</span>
                )}

                {/* View SOP link */}
                {item.sopId && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      navigate(`/operator/sops/${item.sopId}`);
                    }}
                    className="flex items-center gap-1 font-medium text-guild-green"
                  >
                    <FileText size={13} strokeWidth={1.5} />
                    View SOP
                  </button>
                )}

                {/* Talk to coach link — shown for items with a gap description and SOP.
                    Hidden for Ring 2 rotations (cohort framing, no per-operator gap). */}
                {!isCompleted && !isRing2 && item.gapDescription && item.sopId && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      navigate(`/operator/coach?mode=sop_help&sopId=${item.sopId}`);
                    }}
                    className="flex items-center gap-1 font-medium text-guild-green"
                  >
                    <MessageCircle size={13} strokeWidth={1.5} />
                    Talk to coach
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </OperatorCard>
    </div>
  );
}
